// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { fontConfig } from "@vanmeter/XDL/constants";
import { xBaseTheme } from "@vanmeter/XDL/types";
import color from "@vanmeter/XDL/Utilities/color";
import { css } from "styled-components";

const generalStyles = css`
    & + label {
        margin-left: 0;
        margin-right: 4px;
        align-items: center;
    }
    & + label::before {
        flex: 1 0 13px;
        width: 13px;
        height: 13px;
        border-color: ${color("common.ui4")};
        background: ${color("common.ui2")};
        margin-right: 4px;
        margin-top: -1px;
    }
    &:checked + label::after {
        width: 13px;
        height: 13px;
        left: 0;
        top: 2px;
        margin-top: -1px;
    }
    &:focus + label::before {
        box-shadow: none;
    }
`;

const hoverStyle = css`
    &:hover:not(:disabled) + label::before,
    &:hover:checked:not(:disabled) + label::before {
        border-color: ${color("common.ui15")};
    }
    &:hover:not(:disabled) + label {
        cursor: pointer;
    }
    &:hover:checked:not(:disabled) + label::after {
        border: 1px solid ${color("common.ui15")};
        background: ${color("common.ui15")};
    }
`;

const checkedStyle = css`
    &:checked + label::before {
        border-color: ${color("common.ui4")};
        background: ${color("common.ui2")};
    }

    &:checked + label::after {
        background: ${color("common.ui16")};
        outline: 1px solid ${color("common.ui2")};
        outline-offset: -2px;
        border: 1px solid ${color("common.ui4")};
    }
`;

const disabledStyle = css`
    &:disabled + label {
        opacity: 1;
    }
    &:disabled + label::before {
        opacity: 0.33;
    }
    &:disabled + label::after {
        opacity: 0.33;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: fontConfig.xSmall.size,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                user-select: none;
            `,
        },
        css: css`
            input {
                margin-left: 0px;
                margin-right: 0px;
                margin-top: 0px;
                width: 13px;
                height: 13px;
                display: none;
                ${generalStyles}
                ${hoverStyle}
                ${checkedStyle}
                ${disabledStyle}
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        labelProps: {
            variant: "h6",
            as: "legend",
            css: css`
                letter-spacing: 0;
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                margin-bottom: 8px;
                &:empty {
                    display: none;
                }
            }
            ${(props: any) => {
                return props.horizontal
                    ? css`
                          & > ${TypographyStyle as any} {
                              margin-bottom: 4px;
                          }
                          display: inline-block;
                          & > ${RadioStyle} {
                              display: inline-block;
                              margin-top: 0px;
                              margin-right: 16px;
                          }
                      `
                    : css`
                          display: flex;
                          flex-direction: column;
                          ${RadioStyle} {
                              display: flex;
                          }
                          ${RadioStyle} + ${RadioStyle} {
                              margin-top: 12px;
                          }
                      `;
            }}
        `,
    },
} as xBaseTheme["radio"];
